import React, { useState, useEffect } from "react";
import moment from "moment";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useInterviewApi } from "src/_common/hooks/actions/interview/appInterviewApiHook";
import { useScreeningApi } from "src/_common/hooks/actions/screening/appScreeningApiHook";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
import { useUsersManagementApi } from "src/_common/hooks/actions";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useHistory } from "react-router-dom";
import { INTERVIEW_STATUS, CANDIDATE_STATUS } from "src/_config";
import BulkScheduleInterviewModal from "src/components/job-details/Interview/BulkScheduleInterviewModal";
import { isInterviewWithin30Minutes, canStartInterview } from "src/_common/utils/dateTimeUtils";

interface InterviewSchedule {
  approved: {
    schedules: any[];
    total: number;
  };
  pending: {
    schedules: any[];
    total: number;
  };
}

interface Member {
  id: number;
  name: string;
  email: string;
  assign_workspace: string;
  role_id: number;
  employee_code: string;
  designation: string;
  profile_photo_url?: string;
  profile_image_url?: string;
  status: number;
  user_type: string;
  company_id: number;
}

export default function UpcomingInterviewSection() {
  const { t } = useTranslation("dashboard");
  const toast = useToaster();
  const interviewApi = useInterviewApi();
  const screeningApi = useScreeningApi();
  const userDetails = useAppUserDetailsSelector();
  const { callGetUsers } = useUsersManagementApi();
  const history = useHistory();

  const [meetings, setMeetings] = useState<InterviewSchedule>({
    approved: { schedules: [], total: 0 },
    pending: { schedules: [], total: 0 },
  });
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [meetingToReschedule, setMeetingToReschedule] = useState<any>(null);

  // Load members on component mount
  useEffect(() => {
    if (userDetails?.user_type !== "member") {
      loadMembers();
    }
  }, []);

  // Load interview schedule on component mount
  useEffect(() => {
    loadInterviewSchedule();
  }, []);

  // Load interview schedule when selected member changes
  useEffect(() => {
    if (selectedMember) {
      loadInterviewSchedule(selectedMember.id);
    }
  }, [selectedMember]);

  // Function to load members list
  const loadMembers = () => {
    setLoadingMembers(true);
    const params = {
      limit: 100,
      offset: 0,
    };

    callGetUsers(
      params,
      (message: string, data: any) => {
        console.log("Members loaded successfully:", data);
        if (data && data.members) {
          setMembers(data.members);

          const membersWithNames = data.members.filter((m: any) => m.name && m.name.trim());
          if (membersWithNames.length > 0) {
            setSelectedMember(membersWithNames[0]);
          }
        }
        setLoadingMembers(false);
      },
      (error: string) => {
        console.error("Error loading members:", error);
        // toast.error("Failed to load members");
        setLoadingMembers(false);
      },
    );
  };

  // Function to load interview schedule from API
  const loadInterviewSchedule = (member_id?: any) => {
    setLoadingSchedule(true);
    interviewApi.callGetSchedule(
      { ...(member_id ? { member_id } : {}) },
      (msg: any, response: any) => {
        console.log("Interview schedule API response:", response);
        setMeetings(
          response?.interview_schedule || {
            approved: { schedules: [], total: 0 },
            pending: { schedules: [], total: 0 },
          },
        );
        setLoadingSchedule(false);
      },
      (error: any) => {
        console.error("Failed to load interview schedule:", error);
        setLoadingSchedule(false);
      },
    );
  };

  const handleInterviewClick = (meeting: any) => {
    // Navigate to interview schedule page or show details
    history.push("/interview-schedule");
  };

  const handleRescheduleCancelInterview = (meeting: any, status: number) => {
    interviewApi.callRescheduleOrCancel(
      {
        schedule_id: meeting?.id,
        interview_status: status,
      },
      (message: string, resp: any) => {
        toast.success(
          status === INTERVIEW_STATUS.CANCEL
            ? t("dashboard.upcomingInterviews.interviewCancelledSuccess")
            : t("dashboard.upcomingInterviews.interviewRescheduledSuccess"),
        );
        // Refresh the interview schedule
        loadInterviewSchedule(selectedMember?.id);
      },
      (error: string) => {
        toast.error(error || t("dashboard.upcomingInterviews.failedToUpdateInterview"));
        loadInterviewSchedule(selectedMember?.id);
      },
    );
  };

  const handleRescheduleInterview = (meeting: any) => {
    setMeetingToReschedule(meeting);
    setIsScheduleModalOpen(true);
  };

  const handleCancelInterview = (meeting: any) => {
    handleRescheduleCancelInterview(meeting, INTERVIEW_STATUS.CANCEL);
  };

  const handleStartInterview = (meeting: any) => {
    if (meeting?.interview_url) {
      window.open(meeting.interview_url, "_blank");
    }
  };

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setMeetingToReschedule(null);
  };

  const handleScheduleConfirm = (scheduleData: any) => {
    if (!meetingToReschedule?.id) return;

    interviewApi.callRescheduleOrCancel(
      {
        schedule_id: meetingToReschedule.id,
        interview_status: INTERVIEW_STATUS.RESCHEDULE,
        member_id: scheduleData.member_id,
        member_available_time: scheduleData.memberAvailableTime,
      },
      (message: string, resp: any) => {
        toast.success(t("dashboard.upcomingInterviews.interviewRescheduledSuccess"));
        // Update the candidate status to Schedule Interview (Rescheduled)
        if (meetingToReschedule?.screening_id) {
          screeningApi.callUpdateStatus(
            {
              screening_id: meetingToReschedule.screening_id,
              status: CANDIDATE_STATUS.SCHEDULE_INTERVIEW,
            },
            (message: string, resp: any) => {
              console.log("Candidate status updated to Scheduled Interview:", resp);
              loadInterviewSchedule(selectedMember?.id);
              handleCloseScheduleModal();
            },
            (error: string) => {
              toast.error("Failed to update candidate status: " + error);
              loadInterviewSchedule(selectedMember?.id);
              handleCloseScheduleModal();
            },
          );
        } else {
          loadInterviewSchedule(selectedMember?.id);
          handleCloseScheduleModal();
        }
      },
      (error: string) => {
        toast.error(error || t("dashboard.upcomingInterviews.failedToUpdateInterview"));
        loadInterviewSchedule(selectedMember?.id);
        handleCloseScheduleModal();
      },
    );
  };

  // Check if interview is canceled (same logic as Interview tab)
  const isInterviewCanceled = (meeting: any) => {
    return meeting?.interview_status == 0;
  };

  // Check if Start button should be shown
  const shouldShowStartButton = (meeting: any) => {
    // Show start button when:
    // 1. Interview is not canceled
    // 2. Current time is within 30 minutes of interview schedule
    // 3. interview_url exists
    return (
      !isInterviewCanceled(meeting) && isInterviewWithin30Minutes(meeting?.interview_schedule) && meeting?.interview_url
    );
  };

  // Check if Start button should be enabled (within 5 minutes)
  const isStartButtonEnabled = (meeting: any) => {
    return canStartInterview(meeting?.interview_schedule);
  };

  // Check if Reschedule/Cancel buttons should be shown
  const shouldShowRescheduleCancelButtons = (meeting: any) => {
    // Show reschedule/cancel buttons when:
    // 1. Interview is not canceled
    // 2. Start button is not shown
    return !isInterviewCanceled(meeting) && !shouldShowStartButton(meeting);
  };

  // Handle approving pending meeting
  const handleApproveMeeting = (screeningId: string) => {
    console.log("Approve meeting:", screeningId);
    if (!screeningId) {
      toast.error("Meeting information not found.");
      return;
    }

    const requestData = {
      screening_id: screeningId,
      title: "Interview Schedule Accepted",
      description: "Recruiter has accepted the interview schedule and confirmed their availability",
      application_type: 3,
      status: 4,
      member_id: selectedMember?.id ? selectedMember?.id : userDetails?.user?.id,
    };

    console.log("Approve meeting request data:", requestData);
    screeningApi.callAddFeedback(
      requestData,
      (msg: any, response: any) => {
        console.log("Approve meeting API response:", response);
        toast.success(t("dashboard.upcomingInterviews.approveSuccess") || "Interview approved successfully");
        // Reload schedule to get updated data
        loadInterviewSchedule(selectedMember?.id);
      },
      (error: any) => {
        console.error("Failed to approve meeting:", error);
        toast.error(t("dashboard.upcomingInterviews.approveFailed") || "Failed to approve interview");
      },
    );
  };

  // Handle declining pending meeting
  const handleDeclineMeeting = (screeningId: string) => {
    if (!screeningId) {
      toast.error("Meeting information not found.");
      return;
    }

    const requestData = {
      screening_id: screeningId,
      title: "Interview Schedule Declined",
      description: "Recruiter has declined the interview",
      application_type: 3,
      status: 5,
      member_id: selectedMember?.id ? selectedMember?.id : userDetails?.user?.id,
    };

    screeningApi.callAddFeedback(
      requestData,
      (msg: any, response: any) => {
        console.log("Decline meeting API response:", response);
        toast.success(t("dashboard.upcomingInterviews.declineSuccess") || "Interview declined successfully");
        // Reload schedule to get updated data
        loadInterviewSchedule(selectedMember?.id);
      },
      (error: any) => {
        console.error("Failed to decline meeting:", error);
        toast.error(t("dashboard.upcomingInterviews.declineFailed") || "Failed to decline interview");
      },
    );
  };

  return (
    <div className="upcoming-interview-section">
      <div className="section-header">
        <h2 className="section-title">{t("dashboard.upcomingInterviews.title")}</h2>
        {userDetails?.user_type !== "member" && (
          <div className="member-select-container">
            <label htmlFor="member-select" style={{ fontWeight: "500", marginRight: "10px" }}>
              {t("dashboard.upcomingInterviews.memberLabel")}
            </label>
            <select
              id="member-select"
              className="basic-select"
              style={{ minWidth: "200px" }}
              value={selectedMember?.id || ""}
              onChange={(e) => {
                const memberId = parseInt(e.target.value);
                const member = members.find((m) => m.id === memberId);
                setSelectedMember(member || null);
              }}
              disabled={loadingMembers}
            >
              {loadingMembers ? (
                <option>{t("dashboard.upcomingInterviews.loadingMembers")}</option>
              ) : members.length === 0 ? (
                <option>{t("dashboard.upcomingInterviews.noMembers")}</option>
              ) : (
                <>
                  {/* Then add all members */}
                  {members
                    .filter((member) => member.name && member.name.trim())
                    .map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.designation ? `${member.name} - ${member.designation}` : member.name}
                      </option>
                    ))}
                </>
              )}
            </select>
          </div>
        )}
      </div>

      <div className="interview-content">
        {loadingSchedule ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{t("dashboard.upcomingInterviews.loading")}</p>
          </div>
        ) : (
          <>
            {/* Upcoming Approved Meetings */}
            {meetings?.approved?.schedules.filter((m: any) => m.interview_status !== 3).length > 0 && (
              <div className="interview-list">
                <div className="upcoming-interviews-wrapper">
                  {meetings.approved.schedules
                    .filter((m: any) => m.interview_status !== 3)
                    .slice(0, 5)
                    .map((meeting, index) => {
                      const meetingDateTime = meeting.interview_schedule
                        ? moment.utc(meeting.interview_schedule).local()
                        : null;
                      return (
                        <div key={meeting.id || meeting.screening_id || index} className="upcoming-interviews-row">
                          <div className="interview-upcomming-dashboard-wrap">
                            <div className="interview-upcomming-dashboard-cont">
                              {meeting.screening?.job_details?.job_title && (
                                <h3>
                                  <strong>{meeting.screening.job_details.job_title}</strong>
                                </h3>
                              )}
                              <h3>
                                <strong>{t("dashboard.upcomingInterviews.candidateLabel")}</strong>{" "}
                                {meeting.screening?.jobseeker_details?.name ||
                                  t("dashboard.upcomingInterviews.notAvailable")}
                              </h3>
                              <div className="d-flex upcoming-interviews-datetime">
                                <h5>
                                  <i className="interviews-date-icon"></i>{" "}
                                  {meetingDateTime?.isValid()
                                    ? meetingDateTime.format("ddd, MMM DD, YYYY")
                                    : t("dashboard.upcomingInterviews.invalidDate")}
                                </h5>
                                <h5>
                                  <i className="interviews-time-icon"></i>{" "}
                                  {meetingDateTime?.isValid()
                                    ? meetingDateTime.format("h:mm A")
                                    : t("dashboard.upcomingInterviews.invalidTime")}
                                </h5>
                              </div>
                            </div>
                            <div className="upcoming-interviews-btns">
                              {!isInterviewCanceled(meeting) ? (
                                // Show Start/Reschedule/Cancel buttons when interview is not canceled
                                <>
                                  {shouldShowStartButton(meeting) && (
                                    <button
                                      className="btn btn-primary"
                                      // disabled={!isStartButtonEnabled(meeting)}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // if (!isStartButtonEnabled(meeting)) {
                                        //   toast.show(
                                        //     "Interview will be available to start within 5 minutes of the scheduled time. Please check back later.",
                                        //   );
                                        // } else {
                                        handleStartInterview(meeting);
                                        // }
                                      }}
                                    >
                                      <i className="interviews-start-icon"></i>{" "}
                                      {t("dashboard.upcomingInterviews.start")}
                                    </button>
                                  )}
                                  {shouldShowRescheduleCancelButtons(meeting) && (
                                    <>
                                      <button
                                        className="btn btn-secondary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRescheduleInterview(meeting);
                                        }}
                                      >
                                        <i className="interviews-reschedule-icon"></i> Reschedule
                                      </button>
                                      <button
                                        className="btn btn-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCancelInterview(meeting);
                                        }}
                                      >
                                        <i className="interviews-cancel-icon"></i> Cancel
                                      </button>
                                    </>
                                  )}
                                </>
                              ) : (
                                // Show disabled Cancelled button when interview is canceled
                                <button className="btn btn-danger" disabled>
                                  {t("dashboard.upcomingInterviews.cancelled")}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
                {meetings.approved.schedules.length > 5 && (
                  <div className="view-all-link">
                    <button onClick={() => history.push("/interview-schedule")} className="btn-link">
                      {t("dashboard.upcomingInterviews.viewAll") || "View All"} ({meetings.approved.total})
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Pending Schedules Section */}
            {meetings?.pending?.schedules.length > 0 && (
              <div className="interview-list" style={{ marginTop: "30px" }}>
                <h3 className="section-subtitle" style={{ marginBottom: "15px", fontWeight: "600" }}>
                  {t("dashboard.upcomingInterviews.pendingRequests") || "Pending Approval Requests"}
                </h3>
                <div className="upcoming-interviews-wrapper">
                  {meetings.pending.schedules.slice(0, 5).map((meeting, index) => {
                    const meetingDateTime = meeting.interview_schedule
                      ? moment.utc(meeting.interview_schedule).local()
                      : null;
                    return (
                      <div
                        key={meeting.id || meeting.screening_id || index}
                        className="upcoming-interviews-row pending-row"
                      >
                        <div className="interview-upcomming-dashboard-wrap">
                          <div className="interview-upcomming-dashboard-cont">
                            {meeting.screening?.job_details?.job_title && (
                              <h3>
                                <strong>{meeting.screening.job_details.job_title}</strong>
                              </h3>
                            )}
                            <h3>
                              <strong>{t("dashboard.upcomingInterviews.candidateLabel")}</strong>{" "}
                              {meeting.screening?.jobseeker_details?.name ||
                                t("dashboard.upcomingInterviews.notAvailable")}
                            </h3>
                            {meeting.interview_schedule ? (
                              <div className="d-flex upcoming-interviews-datetime">
                                <h5>
                                  <i className="interviews-date-icon"></i>{" "}
                                  {meetingDateTime?.isValid()
                                    ? meetingDateTime.format("ddd, MMM DD, YYYY")
                                    : t("dashboard.upcomingInterviews.invalidDate")}
                                </h5>
                                <h5>
                                  <i className="interviews-time-icon"></i>{" "}
                                  {meetingDateTime?.isValid()
                                    ? meetingDateTime.format("h:mm A")
                                    : t("dashboard.upcomingInterviews.invalidTime")}
                                </h5>
                              </div>
                            ) : null}
                            {meeting.interview_schedule && (
                              <span
                                className="pending-badge"
                                style={{
                                  display: "inline-block",
                                  marginTop: "8px",
                                  padding: "4px 12px",
                                  backgroundColor: "#ffc107",
                                  color: "#000",
                                  borderRadius: "12px",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                }}
                              >
                                {t("dashboard.upcomingInterviews.awaitingApproval") || "Awaiting Approval"}
                              </span>
                            )}
                          </div>
                          <div className="upcoming-interviews-btns">
                            {meeting.interview_schedule ? (
                              <>
                                <button
                                  className="btn btn-success"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApproveMeeting(meeting.screening_id);
                                  }}
                                  title={t("dashboard.upcomingInterviews.approve") || "Approve"}
                                >
                                  <i className="fa fa-check"></i>{" "}
                                  {t("dashboard.upcomingInterviews.approve") || "Approve"}
                                </button>
                                <button
                                  className="btn btn-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeclineMeeting(meeting.screening_id);
                                  }}
                                  title={t("dashboard.upcomingInterviews.decline") || "Decline"}
                                >
                                  <i className="fa fa-times"></i>{" "}
                                  {t("dashboard.upcomingInterviews.decline") || "Decline"}
                                </button>
                              </>
                            ) : (
                              <div
                                className="waiting-confirmation"
                                style={{
                                  padding: "8px 16px",
                                  backgroundColor: "#f8f9fa",
                                  border: "1px solid #dee2e6",
                                  borderRadius: "4px",
                                  color: "#6c757d",
                                  fontSize: "14px",
                                  fontStyle: "italic",
                                }}
                              >
                                {t("dashboard.upcomingInterviews.waitingForJobseekerConfirm") ||
                                  "Waiting for jobseeker to confirm"}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {meetings.pending.schedules.length > 5 && (
                  <div className="view-all-link">
                    <button onClick={() => history.push("/interview-schedule")} className="btn-link">
                      {t("dashboard.upcomingInterviews.viewAll") || "View All"} ({meetings.pending.total})
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* No Interviews Message */}
            {meetings?.approved?.schedules.filter((m: any) => m.interview_status !== 3).length === 0 &&
              meetings?.pending?.schedules.length === 0 && (
                <div className="no-interviews">
                  <i className="fa fa-calendar-times"></i>
                  <p>{t("dashboard.upcomingInterviews.noInterviews")}</p>
                  {userDetails?.user?.is_verified === true && (
                    <button onClick={() => history.push("/interview-schedule")} className="btn-preview">
                      {t("dashboard.upcomingInterviews.scheduleInterview")}
                    </button>
                  )}
                </div>
              )}
          </>
        )}
      </div>

      {/* Schedule Interview Modal */}
      <BulkScheduleInterviewModal
        isOpen={isScheduleModalOpen}
        onClose={handleCloseScheduleModal}
        candidates={
          meetingToReschedule
            ? [
                {
                  id: parseInt(meetingToReschedule.screening_id) || 0,
                  name: meetingToReschedule?.screening?.jobseeker_details?.name || "",
                  screeningId: meetingToReschedule.screening_id || "",
                },
              ]
            : []
        }
        jobId={meetingToReschedule?.job_id || ""}
        onSchedule={handleScheduleConfirm}
      />
    </div>
  );
}
